import { useState } from "react";
import { useQuery } from "react-query";

import { FeatureCode } from "@wallet-manager/node-types";
import {
  WalletStatus,
  WalletType,
} from "@wallet-manager/node-types/dist/src/postgres/const";

import {
  getTronWalletResourceBalanceList,
  getTronWalletResourceBalanceListExport,
  IGetTronWalletResourceBalanceListParams,
} from "../../../../api/tronWalletResourceBalance";
import { useDatePicker } from "../../../../components/DatePicker";
import { useChains } from "../../../../components/FetchConfig";
import GeneralBtn from "../../../../components/GeneralBtn";
import LoadingDialog from "../../../../components/LoadingDialog";
import { Box, Container } from "../../../../components/MuiGenerals";
import {
  useAlerting,
  usePermission,
  useTabs,
  useTranslation,
} from "../../../../hooks";
import { tableConfig } from "../../../../utils/constant";
import {
  downloadFiles,
  findChainInfo,
  getFullApiResponse,
  listMappingTransform,
  toDisplayTime,
} from "../../../../utils/helper";
import { DivideLine, genField } from "../../../../utils/HelperComp";
import { customSx } from "../../../../utils/styling";
import {
  createZusInstance,
  useZusDialogStore,
} from "../../../../zustand/store";
import WalletAddressMultipleTextField from "../components/WalletAddressMultipleTextField";
import WalletDisplayNameMultipleTextField from "../components/WalletDisplayNameMultipleTextField";
import WalletStatusMultipleSelection from "../components/WalletStatusMultipleSelection";
import WalletTypeMultipleSelection from "../components/WalletTypeMultipleSelection";
import TronBalanceRecordList from "./TronBalanceRecordList";

const translatePrefix = "tronWalletResourceBalance";

const initZusParams: IGetTronWalletResourceBalanceListParams = {
  page: 0,
  pageSize: tableConfig.pageSize,
  displayName: [],
  walletType: [],
  chainType: null,
  chainId: null,
  walletAddress: [],
  walletStatus: ["1"],

  // created_date_from: "",
  // created_date_to: "",
  // last_modified_date_from: "",
  // last_modified_date_to: "",
  // chain_type: "",
  // chain_id: "",
  // wallet_address: "",
  // merchant_id: "",
};

export const useZusParams =
  createZusInstance<IGetTronWalletResourceBalanceListParams>(initZusParams);

function BalanceTab() {
  const { tc } = useTranslation(translatePrefix);
  const zusParams = useZusParams();
  const zusDialog = useZusDialogStore();

  const tabsArr = [{ name: tc("table"), value: "" }];

  const { Tabs } = useTabs(tabsArr);

  const listRes = useQuery({
    queryKey: [
      "getTronWalletResourceBalanceList",
      zusParams.body,
      zusParams.refetchCounter,
    ],
    queryFn: async () => {
      zusDialog.openExtra("loadingDialog");
      const res = await getTronWalletResourceBalanceList(zusParams.body);
      zusDialog.closeExtra();

      return res;
    },
  });

  return (
    <Box sx={customSx.layoutBox}>
      <Container disableGutters maxWidth={false}>
        <FilterBar listRes={listRes} />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        maxWidth={false}
        disableGutters
      >
        <Tabs>
          <TronBalanceRecordList listMapping={listMapping} listRes={listRes} />
        </Tabs>
      </Container>
    </Box>
  );
}

export default BalanceTab;

interface IFields {
  walletDisplayName: string;
  walletType: string[];
  chainName: string;
  walletAddress: string;
  walletStatus: string[];
}

const initFields: IFields = {
  walletAddress: "",
  walletDisplayName: "",
  chainName: "",
  walletType: [],
  walletStatus: ["1"],
};

function FilterBar({ listRes }: { listRes: any }) {
  const DateObj = {
    Created: useDatePicker(),
    Modified: useDatePicker(),
  };
  const { hasPermission } = usePermission();
  const [fields, setFields] = useState<IFields>(initFields);
  const { selectChainByPieces } = useChains();
  const { alerting } = useAlerting();
  const { t, te, tc } = useTranslation(translatePrefix);
  const zusParams = useZusParams();
  const zusDialog = useZusDialogStore();

  const getChainNameApiSearchParams = (chain_name: string) => {
    return selectChainByPieces({
      chain_name: chain_name,
    });
  };

  const getParams = (): IGetTronWalletResourceBalanceListParams => {
    const { pageSize } = tableConfig;
    const { chain_type, chain_id } = getChainNameApiSearchParams(
      fields.chainName
    );

    return {
      page: 0,
      pageSize: pageSize,
      displayName: fields.walletDisplayName
        ? fields.walletDisplayName?.split(",")
        : [],
      walletType: fields.walletType,
      chainType: chain_type ? Number(chain_type) : null,
      chainId: chain_id ? Number(chain_id) : null,
      walletAddress: fields.walletAddress
        ? fields.walletAddress?.split(",")
        : [],
      walletStatus: fields.walletStatus,
    };
  };
  const apiParams = getParams();
  const onSearch = () => {
    zusParams.setBody(apiParams);
    zusParams.refetch();
  };
  const onReset = () => {
    setFields(initFields);
    DateObj.Created.clearDate();
    DateObj.Modified.clearDate();
  };

  const onExport = async () => {
    const count = listRes.data?.count || 0;
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      getTronWalletResourceBalanceListExport(
        { ...apiParams, page, pageSize },
        { signal }
      );
    const rawRes = await getFullApiResponse(apiFn, count, true);
    if (rawRes.length === 0) return;
    const res = listMapping("name", rawRes, { t, te, omitKeys: ["rawData"] });
    const config = {};
    downloadFiles(`Balance`, res, config);
  };

  const F = genField({ t }, [
    [
      "walletDisplayName",
      <WalletDisplayNameMultipleTextField
        value={fields.walletDisplayName}
        onChange={(e) => {
          const value = e.target.value;
          setFields((fields) => ({ ...fields, walletDisplayName: value }));
        }}
        label={t("phWalletDisplayName")}
      />,
    ],
    [
      "walletType",
      <WalletTypeMultipleSelection
        value={fields.walletType}
        onChange={(value) => {
          setFields((fields) => ({ ...fields, walletType: value }));
        }}
      />,
    ],
    // [
    //   "chainName",
    //   <ChainNameSingleSelection
    //     value={fields.chainName}
    //     onChange={(value) => {
    //       setFields((fields) => ({ ...fields, chainName: value }));
    //     }}
    //   />,
    // ],
    [
      "wallet_address",
      <WalletAddressMultipleTextField
        value={fields.walletAddress}
        onChange={(e) => {
          const value = e.target.value;
          setFields((fields) => ({ ...fields, walletAddress: value }));
        }}
        label={t("phWallet_address")}
      />,
    ],
    [
      "walletStatus",
      <WalletStatusMultipleSelection
        value={fields.walletStatus}
        onChange={(value) => {
          setFields((fields) => ({ ...fields, walletStatus: value }));
        }}
      />,
    ],
  ]);

  if (listRes.isLoading) return <LoadingDialog forceOpen={true} />;

  return (
    <>
      <Box sx={customSx.filterCmdBar}>
        <GeneralBtn
          label="export"
          isHidden={
            !hasPermission(
              FeatureCode.FeaturesWalletManagement.TronRentResourceBalance
                .TabBalance.Export
            )
          }
          onClick={onExport}
        />
      </Box>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.walletDisplayName}
        {F.walletType}
        {/* {F.chainName} */}
        {F.wallet_address}
        {F.walletStatus}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}

const listMapping = (
  which: "key" | "name",
  array: any[],
  config: { t: any; te: any; omitKeys?: string[] }
): any[][] => {
  const { t, te, omitKeys = [] } = config;

  const res = array.map((item: any, index: number) => {
    const mappedResult = [
      [
        "walletDisplayName",
        t("walletDisplayName"),
        item?.wallet?.display_name || "",
      ],
      ["walletName", t("walletName"), item?.wallet?.wallet_name || ""],
      [
        "walletType",
        t("walletType"),
        te(WalletType[item?.wallet?.wallet_type]) || "",
      ],
      [
        "chainName",
        t("chainName"),
        findChainInfo(item.chain_type, item.chain_id)?.name || "",
      ],
      ["wallet_address", t("wallet_address"), item.wallet_address],
      [
        "walletStatus",
        t("walletStatus"),
        te(WalletStatus[item?.wallet?.status]) || "",
      ],
      ["energy", t("energy"), item.energy],
      ["free_bandwidth", t("free_bandwidth"), item.bandwidth],
      ["delegate_bandwidth", t("delegate_bandwidth"), item.delegated_bandwidth],
      ["last_block_number", t("last_block_number"), item.last_block_number],
      [
        "last_block_time",
        t("last_block_time"),
        toDisplayTime(item.last_block_time),
      ],
      ["created_date", t("created_date"), toDisplayTime(item.created_date)],
      [
        "last_modified_date",
        t("last_modified_date"),
        toDisplayTime(item.last_modified_date),
      ],
      ["rawData", "", item],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });

  const output = res.map(listMappingTransform(which));
  return output;
};
